import { GlobalOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { Button, message, Select } from 'antd';
import { langs } from 'components/Langs';
import StatusButton from 'components/StatusButton';
import TemplateEditor from 'components/TemplateEditor';
import { Concept, getSegmentsLength } from 'components/TemplateEditor/Editor';
import conceptPlugin from 'components/TemplateEditor/lySlatePlugins/conceptPlugin';
import dividingLinePlugin from 'components/TemplateEditor/lySlatePlugins/dividingLinePlugin';
import { mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import ConceptMenu from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import DividingLineButton from 'components/TemplateEditor/toolbarPlugins/DividingLineButton';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { HTMLAttributes, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { TranslateMode } from 'services/api/overseaTranslate';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { Descendant } from 'slate';
import { Hint } from 'views/Setting/AutoEmit/components/ContextEditor';
import { recommendTemplatesNew2Old, translateTemplate } from './utils';
import { RegionLangSelect } from 'components/Langs/RegionLangSelect';
import { useRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';

const Container = styled.div`
  width: 320px;
  border: solid 1px var(--fett-border-default);
  border-bottom: none;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: solid 1px var(--fett-border-default);
  > div {
    display: flex;
    align-items: center;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  padding: 8px 10px;
  border-bottom: solid 1px var(--fett-border-default);
`;

const EditorWrapper = styled.div`
  > div {
    border: none;
  }
  > div > div {
    overflow-x: hidden;
  }
`;

const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 8px;
  background: var(--fett-bg-panel);
  border-bottom: solid 1px var(--fett-border-default) !important;
`;

export interface TranslateToolProps extends HTMLAttributes<HTMLDivElement> {
  region?: General.UpperCaseRegion; // region尽量传，只有在KnowledgeContent.tsx文件所在场景不传
  platform?: General.PlatformUpperCase;
  value: Descendant[];
  onChangeValue?: (value: Descendant[]) => void;
  concepts?: Concept[];
  allowDividing?: boolean;
  recommendTemplates?: Marketing.Policy['recommendTemplates'];
  newRecommendTemplates?: Aide.RecommendTemplate[];
  translateMode: TranslateMode;
  copyTip?: string;
}

export function TranslateTool(props: TranslateToolProps) {
  const {
    region,
    platform,
    value,
    onChangeValue,
    concepts = [],
    allowDividing = false,
    recommendTemplates,
    newRecommendTemplates,
    translateMode,
    copyTip,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const [localLang, setLocalLang] = useState<General.Language>(systemLang);

  const langOptions = useMemo(() => {
    return langs.map((lang) => {
      return {
        value: lang,
        label: t(`langs.${lang}`),
      };
    });
  }, [t]);

  const valueLength = getSegmentsLength(value);

  const getRecommendTemplates = () =>
    recommendTemplatesNew2Old(newRecommendTemplates) || recommendTemplates;

  useMount(() => {
    if (valueLength > 0) {
      translateTemplate(value, localLang, getRecommendTemplates(), translateMode, {
        from: 'translateTool',
      })
        .then(setLocalValue)
        .catch(() => {
          message.error(t('marketing.翻译失败，请稍后再试'));
        });
    }
  });

  const localToolBar = (
    <Toolbar>
      <StatusButton
        type="primary"
        disabled={valueLength === 0}
        onClick={() => {
          return translateTemplate(value, localLang, getRecommendTemplates(), translateMode, {
            from: 'translateTool',
          })
            .then(setLocalValue)
            .catch(() => {
              message.error(t('marketing.翻译失败，请稍后再试'));
            });
        }}
      >
        {t('aide.翻译左侧内容')}
      </StatusButton>
      <div>
        <GlobalOutlined />
        <Select bordered={false} value={localLang} onChange={setLocalLang} options={langOptions} />
      </div>
    </Toolbar>
  );

  const [localValue, setLocalValue] = useState(() => mustache2SlateValue(''));

  const plugins = [];

  const allowConcepts = concepts.length > 0;

  if (allowConcepts) {
    plugins.push(conceptPlugin);
  }

  if (allowDividing) {
    plugins.push(dividingLinePlugin);
  }

  const localEditor = (
    <EditorWrapper>
      <TemplateEditor
        placeholder={t('phrase.请输入回复内容')}
        lySlatePlugins={plugins}
        toolbarChildren={
          (allowConcepts || allowDividing) && (
            <EditorToolbar>
              {allowConcepts && <ConceptMenu concepts={concepts} />}
              {allowDividing && <DividingLineButton />}
            </EditorToolbar>
          )
        }
        value={localValue}
        onChange={setLocalValue}
      />
    </EditorWrapper>
  );

  const localValueLength = getSegmentsLength(localValue);

  const { MERCADO, ...otherLimitLengthMap } = platformExecuteContentLimitLengthMap;

  const limit = platform
    ? platformExecuteContentLimitLengthMap[platform]
    : Math.min(...Object.values(otherLimitLengthMap));

  const localFooter = (
    <Footer>
      <Hint error={String(localValueLength > limit)}>{`${localValueLength}/${limit}`}</Hint>
    </Footer>
  );

  const regionLanguageMap = useRegionLanguageMap();
  const [targetLang, setTargetLang] = useState(region ? regionLanguageMap[region] : 'en-US');

  const targetToolBar = (
    <Toolbar>
      <StatusButton
        type="primary"
        disabled={localValueLength === 0}
        onClick={() => {
          return translateTemplate(localValue, targetLang, getRecommendTemplates(), translateMode, {
            from: 'translateTool',
            platform,
            region,
          })
            .then(setTargetValue)
            .catch(() => {
              message.error(t('marketing.翻译失败，请稍后再试'));
            });
        }}
      >
        {t('aide.翻译上方内容')}
      </StatusButton>
      <div>
        <GlobalOutlined />
        {region && (
          <RegionLangSelect
            bordered={false}
            region={region}
            value={targetLang}
            onChange={setTargetLang}
          />
        )}
      </div>
    </Toolbar>
  );

  const [targetValue, setTargetValue] = useState(() => mustache2SlateValue(''));

  const targetEditor = (
    <EditorWrapper>
      <TemplateEditor
        placeholder={t('phrase.请输入回复内容')}
        lySlatePlugins={plugins}
        toolbarChildren={
          (allowConcepts || allowDividing) && (
            <EditorToolbar>
              {allowConcepts ? <ConceptMenu concepts={concepts} /> : null}
              {allowDividing && <DividingLineButton />}
            </EditorToolbar>
          )
        }
        value={targetValue}
        onChange={setTargetValue}
      />
    </EditorWrapper>
  );

  const targetValueLength = getSegmentsLength(targetValue);

  const targetFooterCopyTop = copyTip || t('aide.复制至最终发送内容');

  const targetFooter = (
    <Footer>
      <Hint error={String(targetValueLength > limit)}>{`${targetValueLength}/${limit}`}</Hint>
      <Button
        disabled={targetValueLength === 0 || !onChangeValue}
        onClick={() => {
          onChangeValue && onChangeValue(targetValue);
        }}
      >
        {targetFooterCopyTop}
      </Button>
    </Footer>
  );

  return (
    <Container {...otherProps}>
      {localToolBar}
      {localEditor}
      {localFooter}
      {targetToolBar}
      {targetEditor}
      {targetFooter}
    </Container>
  );
}
